/** Sign in, join with a code, start a ranch, and settings (one section at a time). */
import { QRCodeSVG } from "qrcode.react";
import { useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { ROLES, ROLE_DESCRIPTIONS, ROLE_LABELS, type Me, type RanchDTO, type Role } from "../../../contract";
import { ApiError, api } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { setLanguage, t, useLanguage, type Language } from "../../lib/i18n";
import { promptInstall, useInstall } from "../../lib/install";
import { clearSnapshots, useConnection } from "../../lib/offline";
import { flush, useOutbox } from "../../lib/outbox";
import { invalidateAll, queryClient, useApi } from "../../lib/query";
import { useCan, useMe } from "../../lib/session";
import { webModules } from "../../registry";
import {
  AppearanceToggle, Button, Card, Chip, Empty, ErrorNote, Field, Icon, IconButton, List, Loading, PageHeader, Row, Section, Segmented, Select, Sheet, TextInput, Toggle, cx, toast,
  undoable, useIsWide,
} from "../../ui/kit";
import { Page } from "../Layout";

/** Marks an English catalog key (collected by the i18n check); translated with t() where shown. */
const tr = (english: string) => english;

function LanguageSwitch() {
  const lang = useLanguage();
  return <Segmented label={t("Language")} value={lang} onChange={(v: Language) => setLanguage(v)} options={[{ value: "en", label: "English" }, { value: "es", label: "Español" }]} />;
}

function AuthFrame({ children, title, subtitle }: { children: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <div className="auth">
      <main className="auth-card">
        <div className="auth-brand"><img src="/favicon.svg" alt="" width={48} height={48} /><span>Hondo</span></div>
        <h1>{title}</h1>
        {subtitle && <p className="subtitle">{subtitle}</p>}
        {children}
        <div className="auth-foot"><LanguageSwitch /><AppearanceToggle /></div>
      </main>
    </div>
  );
}

export function SignIn() {
  useLanguage();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<"signin" | "signup" | "code">("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "", code: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const health = useApi<{ ai: string; demo?: boolean }>("/health");
  if (me?.ranch) return <Navigate to={(location.state as { from?: string } | null)?.from ?? "/"} replace />;
  if (me && !me.ranch) return <Navigate to="/start" replace />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (mode === "signin") await api.post("/auth/signin", { email: form.email, password: form.password });
      if (mode === "signup") await api.post("/auth/signup", { name: form.name, email: form.email, password: form.password });
      if (mode === "code") await api.post("/auth/join", { code: form.code, name: form.name || undefined });
      await clearSnapshots();
      await queryClient.invalidateQueries();
      navigate("/");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."));
    } finally { setBusy(false); }
  };
  const demo = async () => { await api.post("/dev/login"); await clearSnapshots(); await queryClient.invalidateQueries(); navigate("/"); };
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });
  return (
    <AuthFrame title={mode === "signup" ? t("Start using Hondo") : mode === "code" ? t("Join your ranch") : t("Welcome back")} subtitle={t("Say what happened. Hondo keeps the ranch record.")}>
      <form onSubmit={submit} className="auth-form">
        {mode === "code" && <Field label={t("Code from your ranch")}><TextInput value={form.code} onChange={set("code")} autoCapitalize="characters" autoComplete="one-time-code" className="mono code-input" required /></Field>}
        {(mode === "signup" || mode === "code") && <Field label={t("Your name")} optional={mode === "code"}><TextInput value={form.name} onChange={set("name")} autoComplete="name" required={mode === "signup"} /></Field>}
        {mode !== "code" && <>
          <Field label={t("Email")}><TextInput type="email" value={form.email} onChange={set("email")} autoComplete="email" required /></Field>
          <Field label={t("Password")} hint={mode === "signup" ? t("At least 10 characters.") : undefined}><TextInput type="password" value={form.password} onChange={set("password")} autoComplete={mode === "signup" ? "new-password" : "current-password"} minLength={mode === "signup" ? 10 : undefined} required /></Field>
        </>}
        {error && <p className="field-err" role="alert">{error}</p>}
        <Button variant="primary" size="lg" block type="submit" busy={busy}>{mode === "signin" ? t("Sign in") : mode === "signup" ? t("Create account") : t("Join")}</Button>
      </form>
      <div className="auth-switch">
        {mode !== "code" && <button type="button" className="link" onClick={() => setMode("code")}>{t("I have a code from my ranch")}</button>}
        {mode !== "signin" && <button type="button" className="link" onClick={() => setMode("signin")}>{t("I already have an account")}</button>}
        {mode !== "signup" && <button type="button" className="link" onClick={() => setMode("signup")}>{t("Start a new ranch")}</button>}
        {health.data?.demo && <button type="button" className="link muted" onClick={() => void demo().catch(() => toast(t("The demo isn't available here.")))}>{t("Look around the demo ranch")}</button>}
      </div>
    </AuthFrame>
  );
}

export function Join() {
  const { code } = useParams();
  const navigate = useNavigate();
  const preview = useApi<{ ranch: string; role: Role; name: string | null }>(`/invites/${code}`);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (preview.isLoading) return <Loading />;
  return (
    <AuthFrame title={preview.data ? t("Join {{ranch}}", { ranch: preview.data.ranch }) : t("Join your ranch")} subtitle={preview.data ? t(ROLE_DESCRIPTIONS[preview.data.role]) : undefined}>
      {preview.error ? <ErrorNote error={preview.error} /> : (
        <form className="auth-form" onSubmit={async (e) => { e.preventDefault(); setBusy(true); try { await api.post("/auth/join", { code, name: name || undefined }); await clearSnapshots(); await queryClient.invalidateQueries(); navigate("/"); } catch (err) { toast(err instanceof Error ? err.message : "", { tone: "bad" }); } finally { setBusy(false); } }}>
          {!preview.data?.name && <Field label={t("Your name")}><TextInput value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" /></Field>}
          <Button variant="primary" size="lg" block type="submit" busy={busy}>{t("Join")}</Button>
        </form>
      )}
    </AuthFrame>
  );
}

const PRESETS = [
  { id: "basic", label: "Run a cow herd", body: "Calving, health, moves and pasture counts." },
  { id: "seedstock", label: "Raise registered seedstock", body: "Breeding, pedigree, EPDs, registration and bull sales." },
  { id: "commercial", label: "Sell calves or stockers", body: "Weaning, preconditioning, sale lots and buyers." },
  { id: "feedlot", label: "Feed cattle", body: "Receiving, pens, rations, feeding and closeouts." },
  { id: "dairy", label: "Milk cows", body: "Lactations, milk tests, fresh cows and protocols." },
] as const;

export function StartRanch() {
  useLanguage();
  const { data: me, isLoading } = useMe();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [presets, setPresets] = useState<string[]>(["basic"]);
  const [units, setUnits] = useState<"imperial" | "metric">("imperial");
  const [busy, setBusy] = useState(false);
  if (isLoading) return <Loading />;
  if (!me) return <Navigate to="/signin" replace />;
  if (me.ranch) return <Navigate to="/" replace />;
  return (
    <AuthFrame title={t("Tell us about your ranch")} subtitle={t("This only sets what shows up first. Everything stays available.")}>
      <form className="auth-form" onSubmit={async (e) => {
        e.preventDefault(); setBusy(true);
        try { await api.post<Me>("/ranches", { name, presets, units, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, locale: me.person.locale }); await queryClient.invalidateQueries(); navigate("/"); }
        finally { setBusy(false); }
      }}>
        <Field label={t("Ranch name")}><TextInput value={name} onChange={(e) => setName(e.target.value)} required autoFocus /></Field>
        <Field label={t("What do you want to do first?")} hint={t("Pick any that fit.")}>
          <div className="preset-list">
            {PRESETS.map((p) => (
              <label key={p.id} className={`preset ${presets.includes(p.id) ? "preset-on" : ""}`}>
                <input type="checkbox" checked={presets.includes(p.id)} onChange={(e) => setPresets(e.target.checked ? [...presets, p.id] : presets.filter((x) => x !== p.id))} />
                <span><strong>{t(p.label)}</strong><span className="muted">{t(p.body)}</span></span>
              </label>
            ))}
          </div>
        </Field>
        <Field label={t("Units")}><Segmented label={t("Units")} value={units} onChange={setUnits} options={[{ value: "imperial", label: t("Pounds and acres") }, { value: "metric", label: t("Kilograms and hectares") }]} /></Field>
        <Button variant="primary" size="lg" block type="submit" busy={busy} disabled={!name.trim() || !presets.length}>{t("Start")}</Button>
      </form>
    </AuthFrame>
  );
}

// ---------------------------------------------------------------- settings

const SECTIONS = [
  { id: "you", label: tr("You"), hint: tr("Your name, language and appearance"), icon: "people" },
  { id: "device", label: tr("This device"), hint: tr("Install, offline and notes waiting to send"), icon: "phone" },
  { id: "ranch", label: tr("Ranch"), hint: tr("Name, units, time zone and filing"), icon: "ranch" },
  { id: "words", label: tr("Ranch words"), hint: tr("Names and words Hondo should hear right"), icon: "words" },
  { id: "crew", label: tr("Crew and access"), hint: tr("Invite people and choose what they can do"), icon: "people" },
  { id: "more", label: tr("More settings"), hint: tr("Settings for breeding, health, sales and more"), icon: "settings" },
] as const;
type SectionId = (typeof SECTIONS)[number]["id"];

export function Settings() {
  useLanguage();
  const { data: me } = useMe();
  const can = useCan();
  const wide = useIsWide();
  const [params, setParams] = useSearchParams();
  const visible = SECTIONS.filter((s) => (s.id !== "ranch" || can("manage")) && (s.id !== "more" || webModules.some((m) => m.Settings)));
  const asked = params.get("section") as SectionId | null;
  const current = visible.find((s) => s.id === asked)?.id ?? (wide ? "you" : null);
  if (!me?.ranch) return <Loading />;
  const open = (id: SectionId) => setParams({ section: id }, { replace: wide });
  const body = current === "you" ? <YouSettings /> : current === "device" ? <DeviceSettings /> : current === "ranch" ? <RanchSettings ranch={me.ranch} />
    : current === "words" ? <WordsSettings /> : current === "crew" ? <CrewSettings /> : current === "more" ? <ModuleSettings /> : null;

  if (!wide && !current) {
    return (
      <Page>
        <PageHeader back={{ to: "/ranch", label: t("Ranch") }} title={t("Settings")} />
        <List>{visible.map((s) => <Row key={s.id} icon={s.icon} title={t(s.label)} meta={t(s.hint)} onClick={() => open(s.id)} />)}</List>
      </Page>
    );
  }
  const section = visible.find((s) => s.id === current)!;
  return (
    <Page>
      <PageHeader back={wide ? { to: "/ranch", label: t("Ranch") } : { to: "/settings", label: t("Settings") }} title={wide ? t("Settings") : t(section.label)} subtitle={wide ? undefined : t(section.hint)} />
      <div className={cx("settings", wide && "settings-wide")}>
        {wide && (
          <nav className="settings-nav" aria-label={t("Settings")}>
            {visible.map((s) => (
              <button key={s.id} type="button" className={cx("settings-link", s.id === current && "active")} aria-current={s.id === current ? "page" : undefined} onClick={() => open(s.id)}>
                <Icon name={s.icon} size={18} /><span>{t(s.label)}</span>
              </button>
            ))}
          </nav>
        )}
        <div className="settings-body">
          {wide && <header className="settings-head"><h2>{t(section.label)}</h2><p className="muted">{t(section.hint)}</p></header>}
          {body}
        </div>
      </div>
    </Page>
  );
}

function YouSettings() {
  const { data: me } = useMe();
  const [name, setName] = useState(me?.person.name ?? "");
  const lang = useLanguage();
  if (!me) return null;
  return (
    <Card>
      <Field label={t("Your name")} htmlFor="me-name">
        <TextInput id="me-name" value={name} autoComplete="name" onChange={(e) => setName(e.target.value)}
          onBlur={async () => { if (name.trim() && name.trim() !== me.person.name) { await api.patch("/me", { name: name.trim() }); await invalidateAll(); toast(t("Saved"), { tone: "good" }); } }} />
      </Field>
      <Field label={t("Language")} hint={t("Hondo shows screens in this language and answers you in it. What you record is kept as you said it.")}>
        <Segmented label={t("Language")} value={lang} onChange={async (v: Language) => { setLanguage(v); await api.patch("/me", { locale: v }); await invalidateAll(); }} options={[{ value: "en", label: "English" }, { value: "es", label: "Español" }]} />
      </Field>
      <div className="pref-row">
        <span className="pref-text"><label htmlFor="appearance" className="field-label">{t("Dark mode")}</label><span className="field-hint">{t("Saved on this browser. Until you choose, Hondo follows your device.")}</span></span>
        <AppearanceToggle id="appearance" />
      </div>
    </Card>
  );
}

function DeviceSettings() {
  const install = useInstall();
  const outbox = useOutbox();
  const { offline } = useConnection();
  const wide = useIsWide();
  return (
    <div className="stack">
      <Card>
        <h3 className="card-title"><Icon name="download" size={18} />{t("Put Hondo on your home screen")}</h3>
        {install.installed ? <p className="muted"><Chip tone="good" icon="done">{t("Installed")}</Chip> {t("You're using Hondo as an app on this device.")}</p>
          : install.canPrompt ? <><p className="muted">{t("Open Hondo like any other app, full screen, even with weak signal.")}</p><Button variant="primary" icon="download" onClick={() => void promptInstall().then((ok) => ok && toast(t("Hondo is on your home screen."), { tone: "good" }))}>{t("Install Hondo")}</Button></>
          : install.ios ? <ol className="steps"><li>{t("In Safari, tap the Share button.")}</li><li>{t("Choose Add to Home Screen.")}</li><li>{t("Tap Add. Hondo opens from its own icon.")}</li></ol>
          : <p className="muted">{t("Use your browser's menu and choose Install or Add to Home Screen.")}</p>}
      </Card>
      <Card>
        <h3 className="card-title"><Icon name="noSignal" size={18} />{t("Without signal")}</h3>
        <p>{install.offlineReady ? <Chip tone="good" icon="done">{t("Ready to open offline")}</Chip> : <Chip tone="neutral" icon="clock">{t("Getting ready to work offline")}</Chip>}</p>
        <p className="muted">{t("Hondo keeps the app and the last Today, cattle and animal pages you opened on this device. Offline you can look them up and record notes; notes send when you're back.")}</p>
        <div className="row-actions">
          <Button variant="ghost" icon="delete" onClick={() => void clearSnapshots().then(() => toast(t("Saved pages cleared from this device.")))}>{t("Clear saved pages")}</Button>
        </div>
      </Card>
      <Card>
        <h3 className="card-title"><Icon name="upload" size={18} />{wide ? t("Saved on this device") : t("Saved on this phone")}</h3>
        {outbox.length === 0 ? <p className="muted">{t("Everything has been sent.")}</p> : (
          <>
            <p>{t("{{count}} waiting to send.", { count: outbox.length })} {offline ? t("They send by themselves when you're back online.") : null}</p>
            <Button icon="upload" disabled={offline} onClick={() => void flush()}>{t("Send now")}</Button>
          </>
        )}
      </Card>
    </div>
  );
}

function RanchSettings({ ranch }: { ranch: RanchDTO }) {
  const patchRanch = async (body: Partial<RanchDTO> & { settings?: Record<string, unknown> }) => { await api.patch("/ranch", body); await invalidateAll(); toast(t("Saved"), { tone: "good" }); };
  const zones = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [ranch.timezone];
  return (
    <Card>
      <Field label={t("Name")} htmlFor="ranch-name"><TextInput id="ranch-name" defaultValue={ranch.name} onBlur={(e) => e.target.value.trim() && e.target.value !== ranch.name && void patchRanch({ name: e.target.value.trim() })} /></Field>
      <Field label={t("Units")}><Segmented label={t("Units")} value={ranch.units} onChange={(v) => void patchRanch({ units: v })} options={[{ value: "imperial", label: t("Pounds and acres") }, { value: "metric", label: t("Kilograms and hectares") }]} /></Field>
      <Field label={t("Time zone")} htmlFor="ranch-tz" hint={t("Sets what “today” means for the ranch.")}>
        <Select id="ranch-tz" value={ranch.timezone} onChange={(e) => void patchRanch({ timezone: e.target.value })}>{[...new Set([ranch.timezone, ...zones])].map((z) => <option key={z} value={z}>{z.replaceAll("_", " ")}</option>)}</Select>
      </Field>
      <Toggle checked={(ranch.settings.autofile ?? "file") === "file"} onChange={(v) => void patchRanch({ settings: { autofile: v ? "file" : "review" } })}
        label={t("File clear notes automatically")} hint={t("Simple, certain notes are filed right away with Undo. New animals, sales, deaths, money and stock always wait for a look.")} />
    </Card>
  );
}

function ModuleSettings() {
  const parts = webModules.filter((m) => m.Settings);
  if (!parts.length) return <p className="muted">{t("Nothing else to set up yet.")}</p>;
  return <div className="stack">{parts.map((m) => { const S = m.Settings!; return <S key={m.id} />; })}</div>;
}

// ---------------------------------------------------------------- ranch words

interface Word { id: string; term: string; kind: string; sounds_like: string[]; meaning: string | null; source: string; created_at: string }
const WORD_KINDS: Array<{ id: string; label: string }> = [
  { id: "product", label: tr("Product or medicine") }, { id: "place", label: tr("Place") }, { id: "person", label: tr("Person") }, { id: "group", label: tr("Group") },
  { id: "tag", label: tr("Tag or brand") }, { id: "acronym", label: tr("Short form") }, { id: "other", label: tr("Other") },
];
const kindLabel = (k: string) => t(WORD_KINDS.find((x) => x.id === k)?.label ?? "Other");

/** Type a variant and press Enter or a comma to add it. Backspace on an empty box removes the last. */
export function VariantsInput({ value, onChange, id }: { value: string[]; onChange: (v: string[]) => void; id?: string }) {
  const [text, setText] = useState("");
  const add = () => { const v = text.trim().replace(/,$/, ""); if (v && !value.some((x) => x.toLowerCase() === v.toLowerCase())) onChange([...value, v]); setText(""); };
  return (
    <div className="variants">
      {value.map((v) => <Chip key={v} onRemove={() => onChange(value.filter((x) => x !== v))} removeLabel={t("Remove {{name}}", { name: v })}>{v}</Chip>)}
      <input id={id} className="variants-input" value={text} placeholder={value.length ? t("Add another") : t("e.g. vision seven")}
        onChange={(e) => { if (e.target.value.endsWith(",")) { setText(e.target.value); setTimeout(add); } else setText(e.target.value); }}
        onBlur={add}
        onKeyDown={(e: ReactKeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") { e.preventDefault(); add(); }
          else if (e.key === "Backspace" && !text && value.length) onChange(value.slice(0, -1));
        }} />
    </div>
  );
}

function WordForm({ initial, onDone, onCancel }: { initial?: Word; onDone: () => void; onCancel: () => void }) {
  const [term, setTerm] = useState(initial?.term ?? "");
  const [kind, setKind] = useState(initial?.kind ?? "product");
  const [sounds, setSounds] = useState<string[]>(initial?.sounds_like ?? []);
  const [meaning, setMeaning] = useState(initial?.meaning ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form className="word-form" onSubmit={async (e) => {
      e.preventDefault();
      if (!term.trim()) return;
      setBusy(true); setError(null);
      try {
        const body = { term: term.trim(), kind, sounds_like: sounds, meaning: meaning.trim() || null };
        if (initial) await api.patch(`/vocabulary/${initial.id}`, body); else await api.post("/vocabulary", body);
        await invalidateAll();
        toast(initial ? t("Saved") : t("Hondo will listen for “{{term}}”.", { term: term.trim() }), { tone: "good" });
        onDone();
      } catch (err) { setError(err instanceof Error ? err.message : t("Something went wrong on our side. Try again.")); }
      finally { setBusy(false); }
    }}>
      <div className="word-grid">
        <Field label={t("Word or name")} htmlFor="word-term"><TextInput id="word-term" value={term} onChange={(e) => setTerm(e.target.value)} placeholder={t("Vision 7")} required autoFocus /></Field>
        <Field label={t("What it is")} htmlFor="word-kind"><Select id="word-kind" value={kind} onChange={(e) => setKind(e.target.value)}>{WORD_KINDS.map((k) => <option key={k.id} value={k.id}>{t(k.label)}</option>)}</Select></Field>
      </div>
      <Field label={t("Sounds like")} htmlFor="word-sounds" hint={t("How it might be heard or typed. Press Enter after each one.")} optional><VariantsInput id="word-sounds" value={sounds} onChange={setSounds} /></Field>
      <Field label={t("What it means")} htmlFor="word-meaning" optional><TextInput id="word-meaning" value={meaning} onChange={(e) => setMeaning(e.target.value)} placeholder={t("7-way clostridial vaccine")} /></Field>
      {error && <p className="field-err" role="alert">{error}</p>}
      <div className="row-actions"><Button type="submit" variant="primary" icon="check" busy={busy}>{initial ? t("Save") : t("Add word")}</Button><Button variant="ghost" onClick={onCancel}>{t("Cancel")}</Button></div>
    </form>
  );
}

function WordsSettings() {
  const can = useCan();
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const words = useApi<{ items: Word[] }>("/vocabulary");
  const manage = can("manage");
  const needle = q.trim().toLowerCase();
  const shown = (words.data?.items ?? []).filter((w) => !hidden.has(w.id) && (!needle || w.term.toLowerCase().includes(needle) || w.sounds_like.some((s) => s.toLowerCase().includes(needle))));
  const remove = (w: Word) => undoable(t("Removed “{{term}}”.", { term: w.term }), {
    apply: () => setHidden((s) => new Set(s).add(w.id)),
    revert: () => setHidden((s) => { const n = new Set(s); n.delete(w.id); return n; }),
    commit: async () => { await api.del(`/vocabulary/${w.id}`); await invalidateAll(); },
  });
  return (
    <div className="stack">
      <p className="muted">{t("Hondo listens for these when it turns speech into notes, and uses them to match what you say to the right product, place or person.")}</p>
      <div className="list-tools-row">
        <div className="search-input"><Icon name="search" size={18} /><TextInput type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Find a word")} aria-label={t("Find a word")} /></div>
        {manage && !adding && <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>{t("Add a word")}</Button>}
      </div>
      {adding && <Card><WordForm onDone={() => setAdding(false)} onCancel={() => setAdding(false)} /></Card>}
      {words.isLoading ? <Loading /> : words.error && !words.data ? <ErrorNote error={words.error} retry={() => words.refetch()} /> : shown.length === 0 ? (
        <Empty icon="words" title={needle ? t("No words match") : t("No ranch words yet")}
          body={needle ? undefined : t("Add names Hondo might mishear. For example “Vision 7”, which can sound like “vision seven”.")}
          action={manage && !adding && !needle ? <Button icon="plus" onClick={() => setAdding(true)}>{t("Add a word")}</Button> : undefined} />
      ) : (
        <ul className="list words">
          {shown.map((w) => editing === w.id ? (
            <li key={w.id} className="word-row is-editing"><WordForm initial={w} onDone={() => setEditing(null)} onCancel={() => setEditing(null)} /></li>
          ) : (
            <li key={w.id} className="word-row">
              <div className="word-main">
                <span className="word-term">{w.term}</span>
                <span className="word-meta">{kindLabel(w.kind)}{w.source === "learned" ? ` · ${t("learned from a note")}` : ""}{w.meaning ? ` · ${w.meaning}` : ""}</span>
                {w.sounds_like.length > 0 && <span className="word-sounds"><span className="muted small">{t("Sounds like")}</span>{w.sounds_like.map((s) => <Chip key={s}>{s}</Chip>)}</span>}
              </div>
              {manage && (
                <span className="word-tools">
                  <IconButton icon="edit" label={t("Change “{{term}}”", { term: w.term })} onClick={() => setEditing(w.id)} />
                  <IconButton icon="delete" label={t("Remove “{{term}}”", { term: w.term })} onClick={() => remove(w)} />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {!manage && <p className="muted small">{t("Ask an owner or manager to add words.")}</p>}
    </div>
  );
}

// ---------------------------------------------------------------- crew and invitations

interface Invite { code: string; role: Role; name: string | null; expires_at: string }
const joinUrl = (code: string) => `${location.origin}/join/${code}`;

function InviteCode({ invite, onClose }: { invite: Invite; onClose?: () => void }) {
  const url = joinUrl(invite.code);
  const share = async () => {
    try { await navigator.share({ title: t("Join our ranch on Hondo"), text: t("Your code is {{code}}", { code: invite.code }), url }); }
    catch { /* The person closed the share sheet. */ }
  };
  return (
    <div className="invite-show">
      <div className="qr" role="img" aria-label={t("QR code to join with code {{code}}", { code: invite.code })}><QRCodeSVG value={url} size={200} level="M" marginSize={2} bgColor="#FFFFFF" fgColor="#23313D" /></div>
      <p className="invite-big mono" aria-label={t("Code {{code}}", { code: invite.code.split("").join(" ") })}>{invite.code}</p>
      <p className="muted">{t(ROLE_LABELS[invite.role])}{invite.name ? ` · ${invite.name}` : ""} · {t("works until {{date}}", { date: formatDate(invite.expires_at) })}</p>
      <p className="field-hint">{t("They scan this with their phone camera, or open Hondo and type the code.")}</p>
      <div className="row-actions">
        <Button icon="copy" onClick={() => { void navigator.clipboard?.writeText(url).then(() => toast(t("Link copied")), () => toast(url)); }}>{t("Copy link")}</Button>
        {typeof navigator.share === "function" && <Button icon="share" onClick={() => void share()}>{t("Share")}</Button>}
        {onClose && <Button variant="primary" onClick={onClose}>{t("Done")}</Button>}
      </div>
    </div>
  );
}

function InviteSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: me } = useMe();
  const [role, setRole] = useState<Role>("hand");
  const [name, setName] = useState("");
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<Invite | null>(null);
  const close = () => { setMade(null); setName(""); onClose(); };
  const roles = ROLES.filter((r) => r !== "owner" || me?.membership?.role === "owner");
  return (
    <Sheet open={open} onClose={close} title={made ? t("Share this code") : t("Invite someone")} size="sm">
      {made ? <InviteCode invite={made} onClose={close} /> : (
        <form onSubmit={async (e) => {
          e.preventDefault(); setBusy(true);
          try {
            const { code } = await api.post<{ code: string }>("/invites", { role, name: name.trim() || undefined, days });
            await invalidateAll();
            setMade({ code, role, name: name.trim() || null, expires_at: new Date(Date.now() + days * 86_400_000).toISOString() });
          } catch (err) { toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); }
          finally { setBusy(false); }
        }}>
          <Field label={t("Their name")} htmlFor="invite-name" optional hint={t("Leave it blank and they type their own name when they join.")}><TextInput id="invite-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" /></Field>
          <fieldset className="role-pick">
            <legend className="field-label">{t("What they can do")}</legend>
            {roles.map((r) => (
              <label key={r} className={cx("role-option", role === r && "is-on")}>
                <input type="radio" name="role" value={r} checked={role === r} onChange={() => setRole(r)} />
                <span><strong>{t(ROLE_LABELS[r])}</strong><span className="muted">{t(ROLE_DESCRIPTIONS[r])}</span></span>
              </label>
            ))}
          </fieldset>
          <Field label={t("Code works for")} htmlFor="invite-days">
            <Select id="invite-days" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={1}>{t("1 day")}</option><option value={7}>{t("7 days")}</option><option value={30}>{t("30 days")}</option>
            </Select>
          </Field>
          <div className="form-actions"><Button type="submit" variant="primary" size="lg" icon="qr" busy={busy}>{t("Make a code")}</Button></div>
        </form>
      )}
    </Sheet>
  );
}

function CrewSettings() {
  const can = useCan();
  const { data: me } = useMe();
  const people = useApi<{ items: Array<{ id: string; name: string; email: string | null; role: Role; active: boolean }> }>("/people");
  const invites = useApi<{ items: Invite[] }>(can("people") ? "/invites" : null);
  const [inviting, setInviting] = useState(false);
  const [showing, setShowing] = useState<Invite | null>(null);
  const [revoked, setRevoked] = useState<Set<string>>(new Set());
  const active = (invites.data?.items ?? []).filter((i) => !revoked.has(i.code));
  const revoke = (i: Invite) => undoable(t("Code {{code}} turned off.", { code: i.code }), {
    apply: () => setRevoked((s) => new Set(s).add(i.code)),
    revert: () => setRevoked((s) => { const n = new Set(s); n.delete(i.code); return n; }),
    commit: async () => { await api.del(`/invites/${i.code}`); await invalidateAll(); },
  });
  return (
    <div className="stack">
      {can("people") && (
        <Card className="invite-card">
          <div><h3 className="card-title"><Icon name="qr" size={18} />{t("Invite someone")}</h3><p className="muted">{t("Make a code they scan or type. No email needed.")}</p></div>
          <Button variant="primary" icon="plus" onClick={() => setInviting(true)}>{t("Invite someone")}</Button>
        </Card>
      )}
      {active.length > 0 && (
        <Section title={t("Codes waiting to be used")} count={active.length}>
          <ul className="list">
            {active.map((i) => (
              <li key={i.code} className="code-row">
                <span className="mono invite-code-small">{i.code}</span>
                <span className="invite-meta">{t(ROLE_LABELS[i.role])}{i.name ? ` · ${i.name}` : ""}<span className="muted small"> · {t("works until {{date}}", { date: formatDate(i.expires_at) })}</span></span>
                <span className="row-actions">
                  <Button size="sm" variant="ghost" icon="qr" onClick={() => setShowing(i)}>{t("Show")}</Button>
                  <IconButton icon="delete" label={t("Turn off code {{code}}", { code: i.code })} onClick={() => revoke(i)} />
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section title={t("People")} count={people.data?.items.length}>
        {people.isLoading ? <Loading /> : (
          <List>{people.data?.items.map((p) => (
            <Row key={p.id} icon="people" title={<>{p.name}{p.id === me?.membership?.id && <span className="muted small"> · {t("you")}</span>}</>} meta={[t(ROLE_LABELS[p.role]), p.email, p.active ? null : t("No access")].filter(Boolean).join(" · ")}
              trailing={can("people") && p.id !== me?.membership?.id ? (
                <Select aria-label={t("Role for {{name}}", { name: p.name })} value={p.role} onChange={async (e) => {
                  const before = p.role;
                  try {
                    await api.patch(`/people/${p.id}`, { role: e.target.value });
                    await invalidateAll();
                    toast(t("{{name}} is now {{role}}", { name: p.name, role: t(ROLE_LABELS[e.target.value as Role]).toLowerCase() }), { action: { label: t("Undo"), run: () => void api.patch(`/people/${p.id}`, { role: before }).then(invalidateAll) } });
                  } catch (err) { toast(err instanceof Error ? err.message : "", { tone: "bad" }); }
                }}>
                  {ROLES.map((r) => <option key={r} value={r}>{t(ROLE_LABELS[r])}</option>)}
                </Select>
              ) : undefined} />
          ))}</List>
        )}
      </Section>
      <InviteSheet open={inviting} onClose={() => setInviting(false)} />
      <Sheet open={!!showing} onClose={() => setShowing(null)} title={t("Share this code")} size="sm">{showing && <InviteCode invite={showing} onClose={() => setShowing(null)} />}</Sheet>
    </div>
  );
}
